import { z } from 'zod';
import { invoiceSchema } from '@/lib/schemas';
import { InvoiceStatusBadge } from '@/components/invoices/InvoiceStatusBadge';
import { recordedSentAt } from '@/lib/sendEvidence';

type Invoice = z.infer<typeof invoiceSchema>;

export interface InvoiceColumnDef {
  label: string;
  sortable: boolean;
  sortKey?: string;
  align?: 'left' | 'right';
  className?: string;
  render: (invoice: Invoice) => React.ReactNode;
}

/**
 * Phase 10a.9: Column registry for invoice views.
 * Config references string keys; this registry knows how to render them.
 */
export const INVOICE_COLUMN_REGISTRY: Record<string, InvoiceColumnDef> = {
  invoice_number: {
    label: 'Invoice #',
    sortable: true,
    sortKey: 'invoice_number',
    render: (invoice) => (
      <span className="font-medium">{invoice.invoice_number}</span>
    ),
  },

  client_name: {
    label: 'Client',
    sortable: true,
    sortKey: 'client_name',
    render: (invoice) => (
      <span className="truncate block max-w-[200px]">
        {invoice.client_name || '-'}
      </span>
    ),
  },

  project_name: {
    label: 'Project',
    sortable: true,
    sortKey: 'project_name',
    className: 'hidden lg:table-cell',
    render: (invoice) => (
      <span className="truncate block max-w-[200px]">
        {invoice.project_name || '-'}
      </span>
    ),
  },

  total_amount: {
    label: 'Amount',
    sortable: true,
    sortKey: 'total_amount',
    align: 'right',
    render: (invoice) => (
      <span className="tabular-nums">
        ${Number(invoice.total_amount || 0).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
      </span>
    ),
  },

  amount_paid: {
    label: 'Paid',
    sortable: false,
    align: 'right',
    className: 'hidden lg:table-cell',
    render: (invoice) => (
      <span className="tabular-nums text-muted-foreground">
        ${Number(invoice.amount_paid || 0).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        {/* SPEC-invoice-merge-history R4 AC5: cash received above the total is shown as excess held. */}
        {Number(invoice.amount_paid || 0) > Number(invoice.total_amount || 0) && (
          <span className="ml-1 text-xs" data-testid={`excess-paid-${invoice.invoice_number}`}>
            (excess ${(Number(invoice.amount_paid || 0) - Number(invoice.total_amount || 0)).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })})
          </span>
        )}
      </span>
    ),
  },

  status: {
    label: 'Status',
    sortable: true,
    sortKey: 'status',
    render: (invoice) => (
      <InvoiceStatusBadge
        status={invoice.display_status || invoice.status}
        amountPaid={invoice.amount_paid || 0}
        totalAmount={invoice.total_amount}
      />
    ),
  },

  due_date: {
    label: 'Due Date',
    sortable: true,
    sortKey: 'due_date',
    className: 'hidden xl:table-cell',
    render: (invoice) => (
      <span className="text-sm">
        {invoice.due_date
          ? new Date(invoice.due_date + 'T00:00:00').toLocaleDateString('en-CA')
          : '-'}
      </span>
    ),
  },

  email_sent_at: {
    label: 'Sent',
    sortable: false,
    className: 'hidden xl:table-cell',
    render: (invoice) => (
      <span className="text-sm text-muted-foreground">
        {recordedSentAt(invoice)
          ? new Date(recordedSentAt(invoice) as string).toLocaleDateString('en-CA')
          : '-'}
      </span>
    ),
  },
};

/**
 * Resolve config column keys to ordered InvoiceColumnDef[].
 * Silently skips unknown keys for forward compatibility.
 */
export function resolveInvoiceColumns(columnKeys: string[]): { key: string; def: InvoiceColumnDef }[] {
  return columnKeys
    .filter((key) => key in INVOICE_COLUMN_REGISTRY)
    .map((key) => ({ key, def: INVOICE_COLUMN_REGISTRY[key] }));
}
